var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useReducer, useContext, useCallback, useMemo, useEffect } from 'react';
import { usePredictiveSearch } from '../hooks/usePredictiveSearch';
// Initial state
const initialState = {
    query: ((_a = globalThis.location.search.split('q=')[1]) === null || _a === void 0 ? void 0 : _a.split('&')[0]) || '',
    searchResults: {
        products: [],
        collections: [],
        pages: [],
        articles: [],
        queries: [],
    },
    searchItems: [],
    isLoading: false,
    isOpen: false,
    selectedType: null,
    selectedIndex: -1,
    isMobile: false,
};
// Reducer function
function searchReducer(state, action) {
    switch (action.type) {
        case 'SET_QUERY':
            return Object.assign(Object.assign({}, state), { query: action.payload });
        case 'SET_RESULTS':
            return Object.assign(Object.assign({}, state), { searchResults: action.payload });
        case 'SET_SEARCH_ITEMS':
            return Object.assign(Object.assign({}, state), { searchItems: action.payload, selectedIndex: -1 });
        case 'CLEAR_RESULTS':
            return Object.assign(Object.assign({}, state), { searchResults: {
                    products: [],
                    collections: [],
                    pages: [],
                    articles: [],
                    queries: [],
                } });
        case 'SET_LOADING':
            return Object.assign(Object.assign({}, state), { isLoading: action.payload });
        case 'SET_OPEN':
            return Object.assign(Object.assign({}, state), { isOpen: action.payload });
        case 'SET_SELECTED_TYPE':
            return Object.assign(Object.assign({}, state), { selectedType: action.payload });
        case 'SET_SELECTED_INDEX':
            return Object.assign(Object.assign({}, state), { selectedIndex: action.payload });
        case 'SET_MOBILE':
            return Object.assign(Object.assign({}, state), { isMobile: action.payload });
        case 'RESET':
            return initialState;
        default:
            return state;
    }
}
// Create context
const SearchContext = createContext(undefined);
export const SearchProvider = ({ children }) => {
    const [state, dispatch] = useReducer(searchReducer, initialState);
    const { fetchPredictiveSearch } = usePredictiveSearch();
    // Check device type on mount and window resize
    useEffect(() => {
        const checkDeviceType = () => {
            const isMobile = window.innerWidth < 768; // 768px is the standard tablet breakpoint
            dispatch({ type: 'SET_MOBILE', payload: isMobile });
        };
        // Initial check
        checkDeviceType();
        // Add resize listener
        window.addEventListener('resize', checkDeviceType);
        // Cleanup
        return () => window.removeEventListener('resize', checkDeviceType);
    }, []);
    // Action creators
    const setQuery = useCallback((query) => {
        dispatch({ type: 'SET_QUERY', payload: query });
    }, []);
    const setOpen = useCallback((isOpen) => {
        // Don't allow opening search on mobile
        if (state.isMobile) {
            return;
        }
        dispatch({ type: 'SET_OPEN', payload: isOpen });
    }, [state.isMobile]);
    const setSelectedType = useCallback((type) => {
        dispatch({ type: 'SET_SELECTED_TYPE', payload: type });
    }, []);
    const setSelectedIndex = useCallback((index) => {
        dispatch({ type: 'SET_SELECTED_INDEX', payload: index });
    }, []);
    const resetSearch = useCallback(() => {
        dispatch({ type: 'RESET' });
    }, []);
    const clearResults = useCallback(() => {
        dispatch({ type: 'CLEAR_RESULTS' });
    }, []);
    // Helper function to get URL for a search result
    const getResultUrl = useCallback((searchItem) => {
        const { item, type } = searchItem;
        switch (type) {
            case 'PRODUCT':
                return `/products/${item.handle}`;
            case 'COLLECTION':
                return `/collections/${item.handle}`;
            case 'PAGE':
                return `/pages/${item.handle}`;
            case 'ARTICLE':
                return `/blogs${item.blog ? `/${item.blog.handle}` : ''}/${item.handle}`;
            default:
                return '/';
        }
    }, []);
    const searchItems = useCallback((query, types, limit) => __awaiter(void 0, void 0, void 0, function* () {
        // Don't perform search on mobile
        if (state.isMobile) {
            return;
        }
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const results = yield fetchPredictiveSearch(query, types, limit);
            const searchItems = [];
            const payload = {
                products: results.products,
                collections: results.collections.slice(0, 4),
                articles: results.articles.slice(0, 4),
                pages: results.pages.slice(0, 4),
                queries: results.queries,
            };
            payload.products.forEach((product) => {
                searchItems.push({ item: product, type: 'PRODUCT' });
            });
            payload.collections.forEach((collection) => {
                searchItems.push({ item: collection, type: 'COLLECTION' });
            });
            payload.articles.forEach((article) => {
                searchItems.push({ item: article, type: 'ARTICLE' });
            });
            payload.pages.forEach((page) => {
                searchItems.push({ item: page, type: 'PAGE' });
            });
            dispatch({
                type: 'SET_RESULTS',
                payload,
            });
            dispatch({ type: 'SET_SEARCH_ITEMS', payload: searchItems });
        }
        catch (error) {
            clearResults();
        }
        finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }), [fetchPredictiveSearch, clearResults, state.isMobile]);
    const selectItem = useCallback((searchItem) => {
        // Navigate to the appropriate URL based on the result type
        const url = getResultUrl(searchItem);
        window.location.href = url;
        // Reset search state
        setQuery('');
        clearResults();
        setOpen(false);
    }, [getResultUrl, setQuery, setOpen, clearResults]);
    const value = useMemo(() => ({
        state,
        setQuery,
        setOpen,
        setSelectedType,
        setSelectedIndex,
        resetSearch,
        clearResults,
        searchItems,
        selectItem,
        getResultUrl,
    }), [state, setQuery, setOpen, setSelectedType, setSelectedIndex, resetSearch, clearResults, searchItems, selectItem, getResultUrl]);
    return _jsx(SearchContext.Provider, Object.assign({ value: value }, { children: children }));
};
// Custom hook to use the search context
export const useSearch = () => {
    const context = useContext(SearchContext);
    if (context === undefined) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context;
};
